import { GeneralContractorFormData, FilterParams } from './types';
import { GeneralContractorService, CreateGeneralContractorRequest } from '../../services/general-contractor.service';
import { authService } from '../../services/auth.service';
import { geocodeAddress } from '../../utils/geocodeAddress';

// Debounce function for search
export const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export { geocodeAddress as getCoordinatesFromAddress };

// Function to format the form data for API request
export const formatFormDataForAPI = (formData: GeneralContractorFormData): CreateGeneralContractorRequest => {
  // Get current user from auth service
  const currentUser = authService.getUser();
  const userId = currentUser?.id || '';

  // Format the data according to the API requirements
  const apiData: CreateGeneralContractorRequest = {
    name: formData.name,
    active: formData.active,
    userId: userId
  };

  // Check if any address field has been provided or modified
  // If we're editing (formData.id exists), we should send address updates even if fields are being cleared
  const hasAnyAddressData = formData.address?.trim() ||
                            formData.city?.trim() ||
                            formData.province?.trim() ||
                            formData.postalCode?.trim() ||
                            formData.coordinates;

  if (hasAnyAddressData || formData.id) {
    apiData.address = {
      address: formData.address?.trim() || '',
      city: formData.city?.trim() || '',
      province: formData.province?.trim() || '',
      postalCode: formData.postalCode?.trim() || '',
      coordinates: formData.coordinates || null
    };
  }

  return apiData;
};

// Function to check if form data has changed
export const hasFormDataChanged = (originalData: any, currentData: any) => {
  // Helper function to compare objects deeply
  const isEqual = (obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2;
    if (obj1 === null || obj2 === null) return obj1 === obj2;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!isEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  };

  // Compare relevant fields
  const relevantFields = [
    'name', 'address', 'city', 'province', 'postalCode', 'active'
  ];

  for (const field of relevantFields) {
    if (!isEqual(originalData[field], currentData[field])) {
      return true;
    }
  }

  return false;
};

// Function to fetch general contractors with filters
export const fetchGeneralContractors = async (
  params: {
    currentPage: number;
    searchTerm: string;
    pageSize: number;
    sortConfig: { key: string; direction: 'asc' | 'desc' | null };
  },
  callbacks: {
    setLoading: (loading: boolean) => void;
    setGeneralContractors: (generalContractors: any[]) => void;
    setTotalItems: (total: number) => void;
  }
) => {
  const { currentPage, searchTerm, pageSize, sortConfig } = params;
  const { setLoading, setGeneralContractors, setTotalItems } = callbacks;

  setLoading(true);

  try {
    const response = await GeneralContractorService.getGeneralContractorsWithFilters({
      search: searchTerm,
      page: currentPage,
      limit: pageSize,
      sortBy: sortConfig.direction ? sortConfig.key : 'name',
      sortOrder: sortConfig.direction || 'asc'
    });

    setGeneralContractors(response.data || []);
    setTotalItems(response.pagination?.totalItems || 0);
  } catch (error) {
    console.error('Error fetching general contractors:', error);
    setGeneralContractors([]);
    setTotalItems(0);
  } finally {
    setLoading(false);
  }
};

// Function to format address for display
export const formatAddressForDisplay = (generalContractor: any): string => {
  if (!generalContractor.address) return '-';

  const { address, city, province } = generalContractor.address;
  const parts = [];

  if (city) parts.push(city);
  if (province?.name) parts.push(province.name);

  return parts.length > 0 ? parts.join(', ') : '-';
};